import React, { useState, useEffect } from "react";
import { Box, Typography, TextField, Button, Dialog, DialogTitle, DialogContent, DialogActions, Chip, CircularProgress, Divider } from "@mui/material";
import { toast } from "react-toastify";
import Api from "../../api/Api";
import { Urls } from "../../api/Urls";
import { Formik, Form } from "formik";
import AudienceTreeSelect from "../../components/notifications/AudienceTreeSelect";

function CreateNotificationCampaign({ access_token, onClose }) {
  const [channels, setChannels] = useState(["in_app", "push"]);
  const [estimatedCount, setEstimatedCount] = useState(null);
  const [estimating, setEstimating] = useState(false);
  const [loadingPrograms, setLoadingPrograms] = useState(false);
  // Tree-selected multi segments
  const [treeState, setTreeState] = useState({ users: [], segments: [] });

  useEffect(() => {}, []);

  const handleEstimateAudience = async () => {
    setEstimating(true);
    try {
      let count = 0;
      if (treeState.users && treeState.users.length > 0) {
        count = treeState.users.length;
        setEstimatedCount(count);
      } else {
        const response = await Api.post(
          Urls.ESTIMATE_NOTIFICATION_AUDIENCE,
          { targets: treeState.segments || [] },
          { headers: { "content-type": "application/json", authorization: `Bearer ${access_token}` } }
        );
        count = response.data.data.estimated_count;
        setEstimatedCount(count);
      }
      toast.success(`${count} recipient(s) estimated`);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Error estimating audience");
    } finally {
      setEstimating(false);
    }
  };

  const handleChannelChange = (channel) => {
    setChannels((prev) =>
      prev.includes(channel) ? prev.filter((c) => c !== channel) : [...prev, channel]
    );
  };

  const handleSubmit = async (values) => {
    try {
      const payload = {
        name: values.name,
        title: values.title,
        body: values.body || null,
        payload: values.cta_link
          ? {
              cta_link: values.cta_link,
            }
          : null,
        channels: channels,
        ...(treeState.users && treeState.users.length > 0
          ? { targets: [{ type: "user", identifiers: treeState.users }] }
          : { targets: treeState.segments || [] }),
      };

      await Api.post(Urls.CREATE_NOTIFICATION_BROADCAST, payload, {
        headers: { "content-type": "application/json", authorization: `Bearer ${access_token}` },
      });

      toast.success("Campaign created and sent successfully");
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Error creating campaign");
    }
  };

  return (
    <Dialog open={true} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Create Notification Campaign</DialogTitle>
      <Formik
        initialValues={{
          name: "",
          title: "",
          body: "",
          cta_link: "",
        }}
        onSubmit={handleSubmit}
      >
        {({ values, handleChange, handleBlur }) => (
          <Form>
            <DialogContent>
              <Box display="flex" flexDirection="column" gap={2} mt={1}>
                <TextField
                  label="Campaign Name"
                  name="name"
                  value={values.name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  required
                  fullWidth
                />

                <TextField
                  label="Title"
                  name="title"
                  value={values.title}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  required
                  fullWidth
                />

                <TextField
                  label="Message"
                  name="body"
                  value={values.body}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  multiline
                  rows={4}
                  fullWidth
                />

                <TextField
                  label="CTA Link (optional)"
                  name="cta_link"
                  value={values.cta_link}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="/admin/..."
                  fullWidth
                />

                <Divider />

                {/* Tree-style multi selection (replaces the old dropdown) */}
                <AudienceTreeSelect access_token={access_token} onChange={setTreeState} />

                {/* Additional Audiences removed as requested */}

                <Box>
                  <Typography variant="subtitle2" mb={1}>
                    Delivery Channels
                  </Typography>
                  <Box display="flex" gap={1} flexWrap="wrap">
                    {["in_app", "push", "email"].map((channel) => (
                      <Chip
                        key={channel}
                        label={
                          channel === "in_app"
                            ? "In-app"
                            : channel === "push"
                            ? "Push"
                            : "Email"
                        }
                        onClick={() => handleChannelChange(channel)}
                        color={channels.includes(channel) ? "primary" : "default"}
                        variant={channels.includes(channel) ? "filled" : "outlined"}
                      />
                    ))}
                  </Box>
                </Box>

                <Box display="flex" gap={2} alignItems="center">
                  <Button
                    variant="outlined"
                    onClick={handleEstimateAudience}
                    disabled={estimating}
                  >
                    {estimating ? <CircularProgress size={20} /> : "Estimate Audience"}
                  </Button>
                  {estimatedCount !== null && (
                    <Typography variant="body2" color="primary">
                      {estimatedCount} recipient(s) estimated
                    </Typography>
                  )}
                </Box>
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={onClose}>Cancel</Button>
              <Button type="submit" variant="contained" color="primary">
                Create and Send
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
}

export default CreateNotificationCampaign;

